import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setNavigateFunction } from '../utils/navigation';
import { clearAuthStorage } from '../utils/errorHandling';

/**
 * Global Error Handler Component
 * Sets up global error handling for the entire application
 * This ensures that 401 errors are properly handled everywhere
 */
interface GlobalErrorHandlerProps {
  children: React.ReactNode;
}

export const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Set up navigation function for API service
    setNavigateFunction(navigate);

    // Global unhandled promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Check if it's a 401 error that wasn't handled
      if (event.reason?.response?.status === 401) {
        console.warn('Unhandled 401 error detected, clearing auth and redirecting to login');
        
        // Clear localStorage to ensure clean state
        clearAuthStorage();
        
        // Use window.location for reliable redirect
        window.location.href = '/login';
      }
    };

    // Global error handler for uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [navigate]);

  return <>{children}</>;
};
